import { useCallback, useEffect, useMemo, useState } from "react";
import { Ban, CircleCheck, Search, ShieldCheck, UserX } from "lucide-react";
import { adminApi } from "../../lib/adminApi";
import { Input } from "../ui/input";

const STATUS_STYLES = {
  active: "border-gold/40 bg-gold/10 text-gold",
  suspended: "border-yellow-500/40 bg-yellow-500/10 text-yellow-400",
  banned: "border-red-500/40 bg-red-500/10 text-red-400",
};

const ACTIONS = [
  { status: "active", label: "Restore", icon: CircleCheck, testId: "activate", className: "border-gold/40 text-gold hover:bg-gold hover:text-ink" },
  { status: "suspended", label: "Suspend", icon: UserX, testId: "suspend", className: "border-yellow-500/40 text-yellow-400 hover:bg-yellow-500 hover:text-ink" },
  { status: "banned", label: "Ban", icon: Ban, testId: "ban", className: "border-red-500/40 text-red-400 hover:bg-red-500 hover:text-cream" },
];

const MemberRow = ({ entry, busy, onStatus }) => {
  const status = entry.status || "active";
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-cream/5 px-6 py-4 last:border-0" data-testid={`member-row-${entry.id}`}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-cream/85">{entry.email}</p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-cream/40">
          <span>{entry.city || "No city"}</span>
          <span className="flex items-center gap-1">
            <ShieldCheck className="h-3 w-3 text-cream/30" />
            Verification: pending launch
          </span>
          {entry.ref_count > 0 && <span className="text-gold/70">{entry.ref_count} referral{entry.ref_count === 1 ? "" : "s"}</span>}
        </p>
      </div>
      <span className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${STATUS_STYLES[status]}`} data-testid={`member-status-${entry.id}`}>
        {status}
      </span>
      <div className="flex items-center gap-2">
        {ACTIONS.filter((a) => a.status !== status).map((a) => (
          <button
            key={a.status}
            onClick={() => onStatus(entry.id, a.status, entry.email)}
            disabled={busy === entry.id + a.status}
            data-testid={`member-${a.testId}-${entry.id}`}
            className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] transition-colors ${a.className}`}
          >
            <a.icon className="h-3 w-3" /> {a.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const MembersList = ({ entries, filtered, busy, onStatus }) => {
  if (!entries) return <p className="text-sm text-cream/50">Loading members…</p>;
  if (filtered.length === 0) {
    return (
      <p className="rounded-3xl border border-cream/10 px-6 py-8 text-center text-sm text-cream/40" data-testid="members-empty">
        No members match your search.
      </p>
    );
  }
  return (
    <div className="overflow-hidden rounded-3xl border border-cream/10">
      {filtered.map((e) => <MemberRow key={e.id} entry={e} busy={busy} onStatus={onStatus} />)}
    </div>
  );
};

export const MembersTab = () => {
  const [entries, setEntries] = useState(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await adminApi.get("/waitlist");
    setEntries(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status, email) => {
    if (status === "banned" && !window.confirm(`Ban ${email}? They won't be able to rejoin the waitlist.`)) return;
    setBusy(id + status);
    try {
      await adminApi.put(`/admin/users/${id}/status`, { status });
      await load();
    } finally {
      setBusy("");
    }
  };

  const filtered = useMemo(() => {
    if (!entries) return [];
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => e.email.toLowerCase().includes(q) || (e.city || "").toLowerCase().includes(q));
  }, [entries, query]);

  return (
    <div data-testid="members-tab">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-cream/40">
          {filtered.length} member{filtered.length === 1 ? "" : "s"}
        </p>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-cream/40" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search email or city"
            data-testid="members-search"
            className="h-11 w-full rounded-full border-cream/20 bg-cream/5 pl-11 text-sm text-cream placeholder:text-cream/40 focus-visible:ring-2 focus-visible:ring-gold sm:w-72"
          />
        </div>
      </div>

      <MembersList entries={entries} filtered={filtered} busy={busy} onStatus={setStatus} />
      <p className="mt-4 text-xs leading-relaxed text-cream/35">
        Banned emails can't rejoin the waitlist. Full profile details and verification controls activate when the app launches.
      </p>
    </div>
  );
};
